import { useState, useCallback, useEffect } from 'react'
import type { JSX } from 'react'
import { parseAsString, useQueryState } from 'nuqs'

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChannelIcon,
  VisuallyHidden,
  Dialog,
  CsvIcon,
  IconButton,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import { useHasPaymentDetails } from '~publish/contexts/AccountContext'
import {
  useIsTwitterPremium,
  useMastodonMaxCharacters,
  usePinterestBoards,
} from '~publish/hooks/selectors/channel'
import { useChannelPostLimits } from '~publish/hooks/useChannelPostLimits'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { useOnce } from '~publish/hooks/useOnce'
import { useTwentyFourHourTimeFormat } from '~publish/hooks/useTwentyFourHourTimeFormat'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'

import { useMixpanelBulkUploadRecording } from '../../hooks/useMixpanelBulkUploadRecording'
import { CsvUploader } from '../CsvUploader'
import { PostItems } from '../PostItems'
import type { ListItem } from '../PostItems'

import styles from './BulkPostDialog.module.css'

const BulkPostDialogContent = (): JSX.Element | null => {
  const [items, setItems] = useState<ListItem[]>([])
  const channel = useSelectedChannel()

  // Record this session as a bulk-upload-specific Mixpanel replay path while
  // the dialog is open (mounts only when the tool is engaged).
  useMixpanelBulkUploadRecording()
  const channelBoards = usePinterestBoards(channel?.metadata)
  const isTwitterPremium = useIsTwitterPremium(channel?.metadata)
  const mastodonMaxCharacters = useMastodonMaxCharacters(channel?.metadata)

  const twentyFourHourFormat = useTwentyFourHourTimeFormat()
  const { postsRemaining, isFreePlan } = useChannelPostLimits()

  // Track dialog view
  const commonTrackingProperties = useCommonTrackingProperties()
  const hasPaymentDetails = useHasPaymentDetails()
  useOnce((): boolean => {
    if (commonTrackingProperties?.organizationId && channel) {
      BufferTrackerReact.dialogViewed({
        clientName: 'publishWeb',
        dialogName: 'bulkPostUpload',
        product: 'publish',
        ...commonTrackingProperties,
      })
      return true
    }
    return false
  }, [commonTrackingProperties, channel])

  // Track dialog close
  const handleDialogClose = useCallback(
    (closeAction: 'success' | 'cancel') => {
      if (commonTrackingProperties?.organizationId && channel) {
        BufferTrackerReact.modalDismissed({
          modalName: 'bulkPostUpload',
          method: closeAction,
          hasPaymentDetails,
          ...commonTrackingProperties,
        })
      }
    },
    [commonTrackingProperties, channel, hasPaymentDetails],
  )

  // Prevent the dialog from closing when the user clicks outside the dialog content
  // or presses the escape key. The dialog is only dismissable with the provided close actions.
  // This also aids in tracking at which step the dialog close events are triggered.
  const preventUnwantedDialogClose = useCallback(
    (
      e:
        | CustomEvent<{ originalEvent: PointerEvent }>
        | CustomEvent<{ originalEvent: FocusEvent }>
        | KeyboardEvent,
    ) => {
      e.preventDefault()
    },
    [],
  )

  if (!channel) {
    return null
  }

  const handleUploadComplete = (uploadedItems: ListItem[]): string | void => {
    if (isFreePlan && uploadedItems.length > postsRemaining) {
      return `Limit reached: You have ${postsRemaining} posts remaining on the free plan. ${uploadedItems.length} posts found.`
    }
    if (!isFreePlan && uploadedItems.length > 100) {
      return `Limit reached: Cannot upload more than 100 posts at a time. ${uploadedItems.length} posts found.`
    }
    setItems(uploadedItems)
  }

  // Step back through the flow: review -> prepare. The prepare step (which now
  // includes the upload surface) has nowhere to go back to, so the control is
  // hidden there.
  const canGoBack = items.length > 0
  const handleBack = (): void => {
    setItems([])
  }

  return (
    <Dialog.Content
      className={styles.content}
      portal={false}
      onEscapeKeyDown={preventUnwantedDialogClose}
      onPointerDownOutside={preventUnwantedDialogClose}
      onInteractOutside={preventUnwantedDialogClose}
    >
      <Dialog.Header className={styles.header}>
        {canGoBack && (
          <IconButton
            label="Back"
            variant="tertiary"
            size="small"
            className={styles.backButton}
            onClick={handleBack}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
        <CsvIcon className={styles.csvIcon} />
        <ArrowRightIcon color="subtle" />
        <ChannelIcon type={channel.service} color="branded" />
        <VisuallyHidden>
          <Dialog.Description>
            Upload a CSV file to schedule multiple posts in bulk.
          </Dialog.Description>
        </VisuallyHidden>
      </Dialog.Header>
      {items?.length ? (
        <PostItems
          items={items}
          setItems={setItems}
          channelId={channel.id}
          channelName={channel.name}
          channelService={channel.service}
          twentyFourHourFormat={twentyFourHourFormat}
          channelTimezone={channel.timezone}
          onDialogClose={handleDialogClose}
        />
      ) : (
        <CsvUploader
          channelId={channel.id}
          channelService={channel.service}
          channelTimezone={channel.timezone}
          isTwitterPremium={isTwitterPremium}
          mastodonMaxCharacters={mastodonMaxCharacters}
          existingBoards={channelBoards ?? []}
          isFreePlan={isFreePlan}
          postsRemaining={postsRemaining}
          onUploadComplete={handleUploadComplete}
        />
      )}
      <Dialog.CloseButton onClick={() => handleDialogClose('cancel')} />
    </Dialog.Content>
  )
}

/**
 * Bulk Post Upload Dialog
 * Separate the Dialog from the Page component
 * But also contain the Dialog.Content component as whatever renders it,
 * also renders Dialog.Portal which prevents local state from resetting
 */
export const BulkPostDialog = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element | null => {
  // Open the dialog automatically if the bulkUploads query param is true
  const [bulkUploads, setBulkUploads] = useQueryState(
    'bulkUploads',
    parseAsString,
  )
  const [isDialogOpen, setIsDialogOpen] = useState(bulkUploads === 'true')

  // This strips the query param from the url after the dialog is opened to prevent
  // the dialog from opening again when the user navigates back to the page or refreshes
  useEffect(() => {
    if (bulkUploads === 'true') {
      void setBulkUploads(null, { history: 'replace' })
    }
  }, [bulkUploads, setBulkUploads])

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <BulkPostDialogContent />
      </Dialog.Portal>
    </Dialog>
  )
}
